import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { ChevronLeft } from '../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { Button } from '../Button';
import { LogoOverlayPreview, LogoSizeSlider } from '../LogoOverlay';
import { s } from './styles';

const isVideo = (attachment: Api.IFileAttachment) => !!attachment.mimeType?.toLowerCase().startsWith('video/');

interface IProps {
	hideWhiteBackground?: boolean;
	images: Api.IFileAttachment[];
	/** Opens on this image, so enlarging a picture lands on the one that was clicked. */
	initialImageId?: string;
	/** Unset when the account has no logo yet, which the gallery explains rather than drawing a broken image. */
	logoUrl?: string;
	/** Omitted where the size cannot change, which hides the slider. */
	onSizeChange?: (size: number) => void;
	/** False while the overlay is off, or already burned into the media. */
	showLogo: boolean;
	/** The overlay's longest edge, in the image's own pixels. */
	size: number;
}

/**
 * The post's pictures one at a time, with the logo as it will publish. The image dialog's Logo Overlay
 * tab and the preview's enlarged view both show this, so the two cannot disagree about the overlay.
 */
export function LogoOverlayGallery({
	hideWhiteBackground,
	images,
	initialImageId,
	logoUrl,
	onSizeChange,
	showLogo,
	size,
}: IProps) {
	// The overlay is composited onto stills only, so a video in the pool is nothing to preview against.
	const previewable = React.useMemo(() => (images ?? []).filter(image => !isVideo(image)), [images]);
	const [index, setIndex] = React.useState(() =>
		Math.max(
			previewable.findIndex(image => image.id === initialImageId),
			0
		)
	);

	// Enlarging a picture reopens the gallery on it; paging from there is the user's to keep.
	React.useEffect(() => {
		const requested = previewable.findIndex(image => image.id === initialImageId);
		if (requested >= 0) {
			setIndex(requested);
		}
	}, [initialImageId, previewable]);

	const safeIndex = Math.min(index, Math.max(previewable.length - 1, 0));
	const current = previewable[safeIndex];

	const step = (delta: number) => setIndex((safeIndex + delta + previewable.length) % previewable.length);

	return (
		<>
			{showLogo && !logoUrl ? (
				<p className={css(s.logoOverlayWarning)}>
					Your account has no logo yet, so nothing will be overlaid. Add one under Settings to use this.
				</p>
			) : null}
			{!current ? (
				<div className={css(s.logoOverlayEmpty)}>Add an image to this post to preview the overlay.</div>
			) : (
				<>
					<div className={css(s.logoOverlayStage)}>
						{previewable.length > 1 ? (
							<Button aria-label='Previous image' onClick={() => step(-1)} size='icon' type='button' variant='ghost'>
								<ChevronLeft />
							</Button>
						) : null}
						<LogoOverlayPreview
							hideWhiteBackground={hideWhiteBackground}
							imageUrl={current.url}
							logoSizePx={size}
							logoUrl={showLogo ? logoUrl : undefined}
							styles={[s.logoOverlayPreview]}
						/>
						{previewable.length > 1 ? (
							<Button aria-label='Next image' onClick={() => step(1)} size='icon' type='button' variant='ghost'>
								<ChevronRight />
							</Button>
						) : null}
					</div>
					{previewable.length > 1 ? (
						<span aria-live='polite' className={css(s.logoOverlayCount)}>
							{`Image ${safeIndex + 1} of ${previewable.length}`}
						</span>
					) : null}
					{onSizeChange ? (
						<div className={css(s.logoOverlayControls)}>
							<LogoSizeSlider max={Api.MAX_LOGO_SIZE} min={Api.MIN_LOGO_SIZE} onChange={onSizeChange} value={size} />
							<p className={css(s.logoOverlayNote)}>
								The logo is added when the post publishes, at this size on every image. The preview is an approximation.
							</p>
						</div>
					) : null}
				</>
			)}
		</>
	);
}
